/**
 * GraphGrid
 *
 * Renders a responsive grid of Nexus graph panels from a layout, and
 * provides panel management (add / remove / duplicate / rename) plus a
 * chart builder modal with a live preview (FEATURE-01/02/03).
 *
 * Layout changes are surfaced via onLayoutChange so the parent can persist
 * them (per-workspace). Data is fetched per panel via useNexusGraphData.
 *
 * Dependencies:
 * - GraphPanel + useNexusGraphData
 * - lib/nexus/graph-metrics (catalog + config)
 * - UI dialog/select/input/button primitives
 */

'use client'

import { useMemo, useState } from 'react'
import {
  IconChartBar,
  IconCopy,
  IconDotsVertical,
  IconPencil,
  IconPlus,
  IconTrash
} from '@tabler/icons-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { GraphPanel } from '@/components/nexus/graph-panel'
import { useNexusGraphData } from '@/hooks/use-nexus-graph-data'
import {
  NEXUS_METRICS,
  getNexusMetric,
  resolveChartType,
  type NexusChartType,
  type NexusDateRangePreset,
  type NexusGraphLayout,
  type NexusGraphPanelConfig,
  type NexusMetricKey
} from '@/lib/nexus/graph-metrics'

export interface GraphGridProps {
  workspaceId: string
  layout: NexusGraphLayout
  onLayoutChange: (layout: NexusGraphLayout) => void
  className?: string
  // Optional filter overrides merged into every panel (contacts "sync with
  // list filters" support).
  syncedFilters?: Partial<NexusGraphPanelConfig['filters']>
}

let idSeed = 0
function makeId() {
  idSeed += 1
  return `panel-${Date.now().toString(36)}-${idSeed}`
}

// --------------

function ConnectedPanel({
  config,
  workspaceId,
  onConfigChange,
  onRemove,
  onDuplicate,
  onRename,
  onEdit,
  syncedFilters
}: {
  config: NexusGraphPanelConfig
  workspaceId: string
  onConfigChange: (config: NexusGraphPanelConfig) => void
  onRemove: (id: string) => void
  onDuplicate: (id: string) => void
  onRename: (id: string) => void
  onEdit: (id: string) => void
  syncedFilters?: Partial<NexusGraphPanelConfig['filters']>
}) {
  const effectiveConfig = useMemo(
    () =>
      syncedFilters ? { ...config, filters: { ...config.filters, ...syncedFilters } } : config,
    [config, syncedFilters]
  )
  const { data, loading } = useNexusGraphData(effectiveConfig, workspaceId)

  return (
    <div className="group relative">
      <DropdownMenu>
        <DropdownMenuTrigger
          className="absolute right-4 top-4 z-10 rounded-md p-1 text-muted-foreground opacity-60 transition-opacity hover:text-foreground group-hover:opacity-100 data-[state=open]:opacity-100 md:opacity-0"
          aria-label={`Actions for ${config.title}`}
        >
          <IconDotsVertical className="size-4" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => onEdit(config.id)}>
            <IconPencil className="size-4" />
            Edit chart
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onRename(config.id)}>
            <IconPencil className="size-4" />
            Rename
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onDuplicate(config.id)}>
            <IconCopy className="size-4" />
            Duplicate
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => onRemove(config.id)}
            className="text-destructive focus:text-destructive"
          >
            <IconTrash className="size-4" />
            Remove
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <GraphPanel
        config={effectiveConfig}
        data={data}
        loading={loading}
        onConfigChange={(next) =>
          onConfigChange({ ...next, filters: config.filters, id: config.id, title: config.title })
        }
        onRemove={onRemove}
      />
    </div>
  )
}

// --------------

const DATE_RANGE_OPTIONS: { value: NexusDateRangePreset; label: string }[] = [
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' }
]

function ChartBuilderModal({
  open,
  onOpenChange,
  workspaceId,
  initial,
  onSave
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  workspaceId: string
  initial?: NexusGraphPanelConfig
  onSave: (config: NexusGraphPanelConfig) => void
}) {
  const [draft, setDraft] = useState<NexusGraphPanelConfig>(
    () =>
      initial ?? {
        id: makeId(),
        title: getNexusMetric('contacts_added')!.label,
        metric: 'contacts_added',
        chartType: 'line',
        filters: { dateRange: '30d' }
      }
  )

  // Reset the draft whenever the modal opens for a (new) target.
  const openKey = `${open}:${initial?.id ?? 'new'}`
  const [lastOpenKey, setLastOpenKey] = useState(openKey)
  if (openKey !== lastOpenKey) {
    setLastOpenKey(openKey)
    setDraft(
      initial ?? {
        id: makeId(),
        title: getNexusMetric('contacts_added')!.label,
        metric: 'contacts_added',
        chartType: 'line',
        filters: { dateRange: '30d' }
      }
    )
  }

  const metricDef = getNexusMetric(draft.metric)
  const { data, loading } = useNexusGraphData(draft, open ? workspaceId : null)

  const updateMetric = (metric: NexusMetricKey) => {
    const def = getNexusMetric(metric)
    setDraft((d) => ({
      ...d,
      metric,
      chartType: resolveChartType(metric, d.chartType),
      title: d.title || def?.label || metric
    }))
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{initial ? 'Edit chart' : 'Add chart'}</DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1">
            <Label className="text-xs">Title</Label>
            <Input
              value={draft.title}
              onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
              placeholder="Chart title"
            />
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Metric</Label>
            <Select value={draft.metric} onValueChange={(v) => updateMetric(v as NexusMetricKey)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {NEXUS_METRICS.map((m) => (
                  <SelectItem key={m.key} value={m.key}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Chart type</Label>
            <Select
              value={draft.chartType}
              onValueChange={(v) =>
                setDraft((d) => ({
                  ...d,
                  chartType: resolveChartType(d.metric, v as NexusChartType)
                }))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(metricDef?.supportedChartTypes ?? ['line']).map((t) => (
                  <SelectItem key={t} value={t}>
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Date range</Label>
            <Select
              value={draft.filters.dateRange}
              onValueChange={(v) =>
                setDraft((d) => ({
                  ...d,
                  filters: { ...d.filters, dateRange: v as NexusDateRangePreset }
                }))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DATE_RANGE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1 sm:col-span-2">
            <Label className="text-xs">Combine with (split)</Label>
            <Select
              value={draft.split ?? '__none__'}
              onValueChange={(v) =>
                setDraft((d) => ({
                  ...d,
                  split: v === '__none__' ? undefined : (v as NexusMetricKey)
                }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="None" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">None</SelectItem>
                {NEXUS_METRICS.filter((m) => m.key !== draft.metric).map((m) => (
                  <SelectItem key={m.key} value={m.key}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Live preview */}
        <div className="mt-2">
          <p className="mb-2 text-xs font-medium text-muted-foreground">Preview</p>
          <GraphPanel config={draft} data={data} loading={loading} />
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              onSave({ ...draft, title: draft.title.trim() || metricDef?.label || draft.metric })
              onOpenChange(false)
            }}
          >
            {initial ? 'Save chart' : 'Add chart'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// --------------

export function GraphGrid({
  workspaceId,
  layout,
  onLayoutChange,
  className,
  syncedFilters
}: GraphGridProps) {
  const [builderOpen, setBuilderOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState('')

  const editingConfig = useMemo(
    () => (editingId ? layout.panels.find((p) => p.id === editingId) : undefined),
    [editingId, layout.panels]
  )

  const commit = (panels: NexusGraphPanelConfig[]) => {
    onLayoutChange({ version: 1, panels })
  }

  const handleSave = (config: NexusGraphPanelConfig) => {
    const exists = layout.panels.some((p) => p.id === config.id)
    commit(
      exists
        ? layout.panels.map((p) => (p.id === config.id ? config : p))
        : [...layout.panels, config]
    )
    setEditingId(null)
  }

  const handleRemove = (id: string) => {
    commit(layout.panels.filter((p) => p.id !== id))
  }

  const handleDuplicate = (id: string) => {
    const source = layout.panels.find((p) => p.id === id)
    if (!source) return
    const copy: NexusGraphPanelConfig = {
      ...source,
      id: makeId(),
      title: `${source.title} (copy)`
    }
    const index = layout.panels.findIndex((p) => p.id === id)
    const next = [...layout.panels]
    next.splice(index + 1, 0, copy)
    commit(next)
  }

  const handleConfigChange = (config: NexusGraphPanelConfig) => {
    commit(layout.panels.map((p) => (p.id === config.id ? config : p)))
  }

  const startRename = (id: string) => {
    const panel = layout.panels.find((p) => p.id === id)
    if (!panel) return
    setRenamingId(id)
    setRenameValue(panel.title)
  }

  const commitRename = () => {
    if (!renamingId) return
    const title = renameValue.trim()
    if (title) {
      commit(layout.panels.map((p) => (p.id === renamingId ? { ...p, title } : p)))
    }
    setRenamingId(null)
    setRenameValue('')
  }

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
          Graphs
        </h3>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 border-0 text-xs text-muted-foreground shadow-none hover:text-foreground"
          onClick={() => {
            setEditingId(null)
            setBuilderOpen(true)
          }}
        >
          <IconPlus className="mr-1.5 size-3.5" />
          Add chart
        </Button>
      </div>

      {layout.panels.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 rounded-2xl bg-muted/20 p-10 text-center">
          <div className="flex size-10 items-center justify-center rounded-full bg-muted/50 text-muted-foreground">
            <IconChartBar className="size-5" />
          </div>
          <div className="space-y-1">
            <p className="text-sm font-medium text-foreground">No charts yet</p>
            <p className="text-xs text-muted-foreground">
              Add your first chart to visualize CRM metrics over time.
            </p>
          </div>
          <Button
            size="sm"
            variant="secondary"
            className="h-7 border-0 text-xs shadow-none"
            onClick={() => {
              setEditingId(null)
              setBuilderOpen(true)
            }}
          >
            <IconPlus className="mr-1.5 size-4" />
            Add your first chart
          </Button>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {layout.panels.map((panel) => (
            <ConnectedPanel
              key={panel.id}
              config={panel}
              workspaceId={workspaceId}
              onConfigChange={handleConfigChange}
              onRemove={handleRemove}
              onDuplicate={handleDuplicate}
              onRename={startRename}
              onEdit={setEditingId}
              syncedFilters={syncedFilters}
            />
          ))}
        </div>
      )}

      <ChartBuilderModal
        open={builderOpen || Boolean(editingConfig)}
        onOpenChange={(open) => {
          setBuilderOpen(open)
          if (!open) setEditingId(null)
        }}
        workspaceId={workspaceId}
        initial={editingConfig}
        onSave={handleSave}
      />

      {/* Rename dialog */}
      <Dialog open={Boolean(renamingId)} onOpenChange={(open) => !open && setRenamingId(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Rename chart</DialogTitle>
          </DialogHeader>
          <Input
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && commitRename()}
            autoFocus
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRenamingId(null)}>
              Cancel
            </Button>
            <Button onClick={commitRename} disabled={!renameValue.trim()}>
              Rename
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default GraphGrid
